/*
 * Inline article references.
 *
 * Authors cross-link articles with a token rather than a URL:
 *
 *     [[kb:5f8c1b4e-...]]                          link, text = the target's title
 *     [[kb:accoplanning/connecting-a-data-source]] by address
 *     [[kb:...#permissions]]                       with a heading anchor
 *     [[kb:...|see the data source guide]]         with explicit link text
 *
 * Why a token and not a plain markdown link
 * -----------------------------------------
 * A hard-coded /kb/accoplanning/some-slug breaks the moment the address changes,
 * and there is nothing in the database that knows the link exists. A token is
 * resolved at read time, so:
 *
 *   - moving an article cannot break inbound links: the id form never mentioned
 *     the address, and the slug form is rewritten when the address changes
 *   - a link to an article that is hidden, scheduled, unwritten or deleted
 *     degrades to plain text for the public instead of a 404
 *   - references are discoverable, which is what makes "warn me about what
 *     points at this before I delete it" possible at all
 *
 * The id form is the durable one and is what the editor's article picker
 * inserts. The slug form exists because it is readable when someone writes
 * markdown by hand.
 *
 * Why the slug form carries a product
 * -----------------------------------
 * An article's address is the pair (product slug, slug): kb.Article.Slug is
 * unique only within a product, so a bare slug names as many articles as there
 * are products using it. The qualified target is written with a slash, which is
 * what the shape of a URL already suggested and what makes this extension free:
 * CK_Article_Slug has always refused `/`, so a slash in a target could never
 * have matched a stored slug. Every token that resolved before the change is
 * still a token that resolves - there is nothing here that reinterprets text
 * which used to mean something else.
 *
 * A target with no slash still parses, as a slug with NO PRODUCT, and it never
 * resolves. Half an address is not a hint to be completed: picking the one
 * product that happens to own that slug today would silently change meaning the
 * day a second product reuses it, and would make a link's destination depend on
 * data the author cannot see. So it degrades to plain text for readers exactly
 * as an unknown slug does, and the editor's reference check reports it as
 * unresolved, where an author can fix it by naming the product.
 *
 * Everything here is pure string handling, shared by the server (which resolves
 * tokens against the database) and the browser (which renders the result).
 */
import { articleHref } from './articleUrls.js';
/**
 * Matches `[[kb:` + target + optional `|text` + `]]`.
 *
 * The target part excludes `]` and `|` so a token cannot swallow the one that
 * follows it, and excludes newlines so an unclosed token cannot run away over
 * the rest of the document. The text part excludes `]` for the same reason,
 * which means link text cannot itself contain a square bracket - an acceptable
 * trade for a bounded, backtracking-free pattern.
 */
const TOKEN_PATTERN = /\[\[kb:([^\]|\r\n]+?)(?:\|([^\]\r\n]*?))?\]\]/g;
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
 * A qualified slug target: exactly one slash, and both halves spelling a slug
 * the database could actually hold (CK_Article_Slug and CK_Product_Slug, both of
 * which are `[a-z0-9-]`). Applied AFTER lowercasing, so a hand-typed
 * `[[kb:accoPLANNING/Exporting]]` is a qualified target and not a malformed one.
 *
 * Anything else - two slashes, a space, an empty half - is left as a bare,
 * product-less slug rather than rejected, for the reason parseTarget gives: a
 * target that cannot match a row belongs in the reader's text as plain words,
 * not as a raw token nobody can see is broken.
 */
const QUALIFIED_TARGET_PATTERN = /^([a-z0-9-]+)\/([a-z0-9-]+)$/;
/** The canonical prefix, exported so the editor toolbar does not re-spell it. */
export const ARTICLE_LINK_PREFIX = '[[kb:';
/**
 * The key a resolution map is built on, from either side: the parsed token, or
 * the (ProductSlug, Slug) of a row.
 *
 * BOTH sides must go through this function, and that is the whole point of it
 * being a function. The article slug arrives from the database with a lowercase
 * guarantee behind it; the product segment of a token or a route parameter has
 * no such guarantee at all, so two maps built with two spellings of "normalise"
 * would agree in every test and miss in production the first time someone typed
 * a capital letter.
 *
 * The key is the qualified target's own spelling, `<product>/<slug>`, which is
 * not an accident: it is also the needle the server prefilters revision text
 * with, so there is one string to build and not two. It is unambiguous because
 * neither half may contain a slash - see QUALIFIED_TARGET_PATTERN.
 */
export function articleLinkTargetKey(productSlug, slug) {
    return `${productSlug.trim().toLowerCase()}/${slug.trim().toLowerCase()}`;
}
/**
 * The same key for a parsed token, or null when the token cannot name an article
 * at all: an id-form token, or a bare slug with no product. A caller that gets
 * null has its answer - the token does not resolve - without having to know why.
 */
export function articleLinkTokenKey(token) {
    if (token.kind !== 'slug' || !token.productSlug || !token.slug)
        return null;
    return articleLinkTargetKey(token.productSlug, token.slug);
}
export function isGuid(value) {
    return GUID_PATTERN.test(value.trim());
}
/**
 * Builds the token an editor should insert for a given article. Always uses the
 * id form, because that is the form that survives an address change - either
 * half of it.
 */
export function buildArticleLinkToken(articleId, options = {}) {
    const fragment = options.fragment ? `#${options.fragment}` : '';
    const text = options.text ? `|${sanitiseTokenText(options.text)}` : '';
    return `${ARTICLE_LINK_PREFIX}${articleId.toLowerCase()}${fragment}${text}]]`;
}
/**
 * Strips the characters that would terminate a token early, so link text taken
 * from a title can never produce a malformed token.
 */
export function sanitiseTokenText(text) {
    return text
        .replace(/[\]|\r\n]+/g, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim();
}
/**
 * Finds every article-link token in a markdown document.
 *
 * Tokens inside fenced or inline code are skipped: a knowledge base article
 * explaining this very syntax has to be able to show a token without it turning
 * into a link. Duplicated tokens are all returned, each with its own offset.
 */
export function parseArticleLinkTokens(markdown) {
    if (!markdown || !markdown.includes(ARTICLE_LINK_PREFIX))
        return [];
    const masked = maskMarkdownCode(markdown);
    const tokens = [];
    TOKEN_PATTERN.lastIndex = 0;
    let match;
    while ((match = TOKEN_PATTERN.exec(masked)) !== null) {
        const index = match.index;
        // Read the token text from the original string, not the masked copy, so
        // the recorded token is byte-identical to what the author wrote.
        const token = markdown.slice(index, index + match[0].length);
        const parsed = parseTarget(match[1] ?? '', match[2]);
        if (parsed)
            tokens.push({ ...parsed, token, index });
    }
    return tokens;
}
/**
 * Distinct targets, for batching the database lookups behind resolution.
 *
 * Slug targets come back as PAIRS, keyed and deduplicated by
 * `articleLinkTargetKey`, because a lookup by slug alone would be a lookup for
 * an article the token may not have meant. Product-less targets are left out
 * entirely: they can never match a row, so asking the database about them would
 * be a round trip whose answer is already known.
 */
export function collectArticleLinkTargets(tokens) {
    const articleIds = new Set();
    const slugTargets = new Map();
    for (const token of tokens) {
        if (token.kind === 'id' && token.articleId) {
            articleIds.add(token.articleId.toLowerCase());
            continue;
        }
        // The key decides; the two field checks that follow are for the type
        // checker, which cannot see that a non-null key implies both halves. The
        // pair is stored as parsed rather than as the key's halves because the
        // parser has already lowercased it and QUALIFIED_TARGET_PATTERN admits no
        // whitespace - the two are the same string.
        const key = articleLinkTokenKey(token);
        if (key && token.productSlug && token.slug) {
            slugTargets.set(key, { productSlug: token.productSlug, slug: token.slug });
        }
    }
    return { articleIds: [...articleIds], slugTargets: [...slugTargets.values()] };
}
/**
 * Substitutes resolved tokens for markdown links, leaving the rest of the
 * document untouched.
 *
 * Resolutions are matched by offset, which is why `parseArticleLinkTokens`
 * records one: the same token can appear many times, and each occurrence has to
 * be replaced where it stands rather than by a global search. Replacement runs
 * back to front so earlier offsets stay valid.
 *
 * The href is built by `articleHref` and cannot be supplied by the caller. It
 * used to be a `buildHref` option, and every caller passed the same one-line
 * template literal - which is precisely how a link ends up on a URL shape the
 * rest of the site has moved off. There is one address for an article; this
 * renders it.
 */
export function renderArticleLinks(markdown, resolutions, options = {}) {
    if (resolutions.length === 0)
        return markdown;
    const ordered = [...resolutions].sort((a, b) => b.index - a.index);
    const renderUnresolved = options.renderUnresolved ?? defaultRenderUnresolved;
    let output = markdown;
    for (const resolution of ordered) {
        // Guard against a resolution list that has drifted from the text.
        if (output.slice(resolution.index, resolution.index + resolution.token.length) !== resolution.token) {
            continue;
        }
        const replacement = renderResolved(resolution) ?? renderUnresolved(resolution);
        output =
            output.slice(0, resolution.index) +
                replacement +
                output.slice(resolution.index + resolution.token.length);
    }
    return output;
}
/**
 * The markdown link for a resolved target, or null when there is nothing to link
 * to and the caller should fall back to plain text.
 *
 * Null covers the unresolved case and one more: a resolution flagged resolved
 * that is missing half of its target address. That is a server bug rather than
 * an author's mistake, but the failure it would otherwise produce is a link to
 * /kb//something - a dead address in a reader's page, which is the exact thing
 * the token grammar exists to prevent. Degrading to plain text keeps the
 * sentence readable while the bug is found.
 */
function renderResolved(resolution) {
    if (!resolution.resolved)
        return null;
    if (!resolution.targetProductSlug || !resolution.targetSlug)
        return null;
    const href = articleHref(resolution.targetProductSlug, resolution.targetSlug, resolution.fragment);
    const label = resolution.text ?? resolution.targetTitle ?? resolution.targetSlug;
    return `[${escapeMarkdownText(label)}](${encodeMarkdownHref(href)})`;
}
function defaultRenderUnresolved(resolution) {
    const label = resolution.text ?? resolution.targetTitle ?? resolution.slug ?? '';
    return escapeMarkdownText(label);
}
/**
 * Removes the tokens that point at a given article, replacing each with the
 * plain text it would have displayed.
 *
 * This is what "clear any references to it" does when an article is deleted:
 * the sentence keeps reading, but there is no link and no orphaned token left
 * behind. Used by the server against the current and latest revisions of every
 * referencing article.
 *
 * One of the two rewrites there are - renameArticleLinks is the other. A token
 * only ever names an article, so once that article is gone there is nothing left
 * for the token to mean and nothing to rewrite it into.
 */
export function clearArticleLinks(markdown, matches, labelFor) {
    const tokens = parseArticleLinkTokens(markdown).filter(matches);
    if (tokens.length === 0)
        return { content: markdown, removed: 0 };
    // Back to front, so the offsets of the tokens still to come stay valid.
    let output = markdown;
    for (const token of [...tokens].sort((a, b) => b.index - a.index)) {
        const label = escapeMarkdownText(token.text ?? labelFor(token));
        output = output.slice(0, token.index) + label + output.slice(token.index + token.token.length);
    }
    return { content: output, removed: tokens.length };
}
/**
 * Points the tokens that name an article at `previous` at `next` instead.
 *
 * This is what an ADDRESS change does to the other articles, and an address has
 * two halves: a new slug, a move to another product, or both at once are the
 * same rewrite here, because a token spells the pair and does not care which
 * half of it moved. The id form never mentioned the address and needs nothing,
 * but `[[kb:accoplanning/old-address]]` would stop resolving the moment either
 * column changed, and the reader would get plain text where a link had been.
 * Used by the server against the current and latest revisions of every other
 * article, exactly as clearArticleLinks is on delete.
 *
 * Product-less tokens are NOT rewritten, even when their slug matches. They
 * never resolved, so there is no link to preserve, and adopting one would be the
 * guess this grammar refuses to make - on the strength of a rename, of all
 * moments, when the article involved is demonstrably moving around.
 *
 * The token is rebuilt from its parsed parts rather than patched in place, so a
 * fragment and link text survive verbatim and a hand-typed variant - upper case,
 * stray spaces - comes out in the canonical spelling. Matching is on the parsed
 * pair, which the parser lowercases, so `[[kb:accoPLANNING/Old-Address]]` is
 * renamed too.
 */
export function renameArticleLinks(markdown, previous, next) {
    const wanted = articleLinkTargetKey(previous.productSlug, previous.slug);
    const replacementTarget = articleLinkTargetKey(next.productSlug, next.slug);
    if (wanted === replacementTarget)
        return { content: markdown, renamed: 0 };
    const tokens = parseArticleLinkTokens(markdown).filter((token) => articleLinkTokenKey(token) === wanted);
    if (tokens.length === 0)
        return { content: markdown, renamed: 0 };
    // Back to front, so the offsets of the tokens still to come stay valid.
    let output = markdown;
    for (const token of [...tokens].sort((a, b) => b.index - a.index)) {
        const fragment = token.fragment ? `#${token.fragment}` : '';
        const text = token.text ? `|${token.text}` : '';
        // The key IS the qualified target's spelling, so the new token is the
        // prefix, the key and the parts that survived.
        const replacement = `${ARTICLE_LINK_PREFIX}${replacementTarget}${fragment}${text}]]`;
        output =
            output.slice(0, token.index) + replacement + output.slice(token.index + token.token.length);
    }
    return { content: output, renamed: tokens.length };
}
/**
 * How many distinct articles among `references` link to an article by the given
 * ADDRESS - the ones a change of slug or product will rewrite. A reference
 * carrying only id-form tokens is not counted: those need nothing. Nor is one
 * carrying a product-less token that happens to name the same slug: it does not
 * resolve now and will not be rewritten.
 *
 * For the editor's warning before a save. The list it reads is the sample the
 * detail response carries, so past the sample size this is a lower bound; the
 * count reported after the save is exact.
 */
export function countArticlesLinkingBySlug(references, target) {
    const wanted = articleLinkTargetKey(target.productSlug, target.slug);
    const articles = new Set();
    for (const reference of references) {
        const linksBySlug = reference.tokens.some((raw) => parseArticleLinkTokens(raw).some((token) => articleLinkTokenKey(token) === wanted));
        if (linksBySlug)
            articles.add(reference.articleId.toLowerCase());
    }
    return articles.size;
}
function parseTarget(rawTarget, rawText) {
    const [targetPart, ...fragmentParts] = rawTarget.split('#');
    const target = (targetPart ?? '').trim();
    if (target.length === 0)
        return null;
    const fragment = fragmentParts.join('#').trim();
    const text = rawText?.trim();
    // Anything that is not a GUID is a slug target, lowercased, and qualified by a
    // product when the author wrote one.
    //
    // Deliberately no REJECTION on the slug character class: a target that cannot
    // match any row should reach resolution and be reported as not found, rather
    // than being thrown away here and left in the reader's text as a raw token.
    // The class is used only to decide whether the slash is a qualifier, so
    // "kb:see/the/manual" stays one unresolvable slug instead of becoming a
    // product called "see".
    const parsed = isGuid(target)
        ? { kind: 'id', articleId: target.toLowerCase() }
        : toSlugTarget(target.toLowerCase());
    if (fragment)
        parsed.fragment = fragment;
    if (text)
        parsed.text = text;
    return parsed;
}
/**
 * A lowercased, non-GUID target as a slug token: qualified when it spells an
 * address, product-less when it does not.
 *
 * The product-less case keeps the WHOLE target as the slug, slashes and all,
 * rather than the part before the first slash. It is what the author typed, it
 * is what the editor's unresolved-reference list has to show them, and it can
 * never be mistaken for an address because a stored slug cannot contain a slash.
 */
function toSlugTarget(target) {
    const qualified = QUALIFIED_TARGET_PATTERN.exec(target);
    if (!qualified)
        return { kind: 'slug', slug: target };
    return { kind: 'slug', productSlug: qualified[1] ?? '', slug: qualified[2] ?? '' };
}
/**
 * Replaces the contents of fenced blocks, indented blocks and inline code spans
 * with ` ` padding of identical length, so offsets are preserved while
 * tokens inside code become invisible to the pattern.
 *
 * Exported because articleImages.ts scans the same documents for `![](...)`
 * references and has to agree, character for character, about what counts as
 * code. Two scanners with their own idea of that would show an author a link
 * inside a code sample one day and an image inside one the next.
 */
export function maskMarkdownCode(markdown) {
    const chars = markdown.split('');
    const lines = markdown.split('\n');
    let offset = 0;
    let fenceMarker = null;
    for (const line of lines) {
        const lineStart = offset;
        offset += line.length + 1; // + newline
        const fenceMatch = /^\s{0,3}(`{3,}|~{3,})/.exec(line);
        if (fenceMarker === null && fenceMatch) {
            fenceMarker = (fenceMatch[1] ?? '').charAt(0).repeat(3);
            blank(chars, lineStart, line.length);
            continue;
        }
        if (fenceMarker !== null) {
            blank(chars, lineStart, line.length);
            if (fenceMatch && (fenceMatch[1] ?? '').startsWith(fenceMarker))
                fenceMarker = null;
            continue;
        }
        // An indented code block: four spaces or a tab, and not a list continuation
        // we can distinguish cheaply. Treating it as code is the safe direction.
        if (/^(\t| {4})/.test(line) && line.trim().length > 0) {
            blank(chars, lineStart, line.length);
            continue;
        }
        maskInlineCode(chars, line, lineStart);
    }
    return chars.join('');
}
function maskInlineCode(chars, line, lineStart) {
    const runs = [];
    const backtickRun = /`+/g;
    let runMatch;
    while ((runMatch = backtickRun.exec(line)) !== null) {
        runs.push({ start: runMatch.index, length: runMatch[0].length });
    }
    // A code span is delimited by two backtick runs of equal length. Walk the runs
    // left to right, pairing each opener with the next matching closer and
    // resuming after it, so nested-looking backticks cannot double-count.
    let i = 0;
    while (i < runs.length) {
        const open = runs[i];
        if (!open) {
            i += 1;
            continue;
        }
        let closeIndex = -1;
        for (let j = i + 1; j < runs.length; j += 1) {
            if (runs[j]?.length === open.length) {
                closeIndex = j;
                break;
            }
        }
        const close = closeIndex === -1 ? undefined : runs[closeIndex];
        if (!close) {
            i += 1;
            continue;
        }
        const from = open.start + open.length;
        blank(chars, lineStart + from, close.start - from);
        i = closeIndex + 1;
    }
}
function blank(chars, start, length) {
    for (let i = start; i < start + length && i < chars.length; i += 1) {
        chars[i] = ' ';
    }
}
/**
 * Escapes the characters that could turn substituted text into markdown
 * structure in an inline position: emphasis, code spans, links, raw HTML.
 *
 * Deliberately conservative - escaping `-`, `#` or `.` as well would be
 * harmless to a renderer but litters the stored markdown that an editor opens
 * next, and those characters only carry meaning at the start of a line, where
 * substituted link text does not land.
 */
export function escapeMarkdownText(text) {
    return text.replace(/([\\`*_[\]<>])/g, '\\$1');
}
/**
 * Encodes an href for markdown. Wraps in angle brackets when it contains
 * characters that would otherwise end the link target early, which is what
 * protects against a slug or fragment injecting markdown structure.
 *
 * Exported for the image rewriter, which puts a URL in the same position inside
 * `![alt](...)` and needs the same protection.
 */
export function encodeMarkdownHref(href) {
    if (/[()\s<>]/.test(href)) {
        return `<${href.replace(/([<>\\])/g, '\\$1')}>`;
    }
    return href;
}
//# sourceMappingURL=articleLinks.js.map